import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import { useFinanceListHook } from '../hooks/useFinanceListHook';
import {
  TFinanceDetails,
  TFinancePrimary,
} from '../../../../apis/finance/finance.interfaces';

type TFinanceAppState = {
  errorMessage?: string;
  isLoading: boolean;
  requestParams: TPaginationParams;
  financeList: TFinancePrimary[];
  isManageFinanceItemOpen: boolean;
  isFinanceItemLoading: boolean;
  isFinanceItemError: boolean;
  financeItemErrorMessage: string;
  selectedFinanceItem: TFinanceDetails | null;
  selectedFinanceItemId: string | null;
  isCreateMode: boolean;
  isEditMode: boolean;
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_FINANCE_LIST = 'UPDATE_FINANCE_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_IS_MANAGE_FINANCE_ITEM_OPEN = 'SET_IS_MANAGE_FINANCE_ITEM_OPEN',
  SET_SELECTED_FINANCE_ITEM = 'SET_SELECTED_FINANCE_ITEM',
  SET_SELECTED_FINANCE_ITEM_ID = 'SET_SELECTED_FINANCE_ITEM_ID',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_FINANCE_ITEM_LOADING = 'SET_IS_FINANCE_ITEM_LOADING',
  SET_IS_FINANCE_ITEM_ERROR = 'SET_IS_FINANCE_ITEM_ERROR',
  SET_FINANCE_ITEM_ERROR_MESSAGE = 'SET_FINANCE_ITEM_ERROR_MESSAGE',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_FINANCE_LIST; payload: TFinancePrimary[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_IS_MANAGE_FINANCE_ITEM_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_FINANCE_ITEM_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_FINANCE_ITEM_ERROR; payload: boolean }
  | { type: ACTION_TYPE.SET_FINANCE_ITEM_ERROR_MESSAGE; payload: string }
  | {
      type: ACTION_TYPE.SET_SELECTED_FINANCE_ITEM;
      payload: TFinanceDetails | null;
    }
  | { type: ACTION_TYPE.SET_SELECTED_FINANCE_ITEM_ID; payload: string | null }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean };

const initialFinanceAppState: TFinanceAppState = {
  financeList: [],
  errorMessage: undefined,
  isLoading: false,
  requestParams: {
    page: 1,
    limit: 10,
  },
  isManageFinanceItemOpen: false,
  isFinanceItemLoading: false,
  isFinanceItemError: false,
  financeItemErrorMessage: '',
  selectedFinanceItem: null,
  selectedFinanceItemId: null,
  isCreateMode: false,
  isEditMode: false,
};

type TState = {
  financeAppState: TFinanceAppState;
  financeList: TFinancePrimary[];
};

const FinanceContext = createContext<TState | undefined>(undefined);
const FinanceDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const FinanceReducer = (
  state: TFinanceAppState,
  action: Action
): TFinanceAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        requestParams: { ...state.requestParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_FINANCE_LIST:
      return { ...state, financeList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_IS_MANAGE_FINANCE_ITEM_OPEN:
      return { ...state, isManageFinanceItemOpen: action.payload };
    case ACTION_TYPE.SET_IS_FINANCE_ITEM_LOADING:
      return { ...state, isFinanceItemLoading: action.payload };
    case ACTION_TYPE.SET_IS_FINANCE_ITEM_ERROR:
      return { ...state, isFinanceItemError: action.payload };
    case ACTION_TYPE.SET_FINANCE_ITEM_ERROR_MESSAGE:
      return { ...state, financeItemErrorMessage: action.payload };
    case ACTION_TYPE.SET_SELECTED_FINANCE_ITEM:
      return { ...state, selectedFinanceItem: action.payload };
    case ACTION_TYPE.SET_SELECTED_FINANCE_ITEM_ID:
      return { ...state, selectedFinanceItemId: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...state, isCreateMode: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const FinanceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [financeAppState, dispatch] = useReducer(
    FinanceReducer,
    initialFinanceAppState
  );

  const {
    data: financeResponse,
    isLoading: financeResponseLoading,
    isError: financeResponseLoadingError,
    isSuccess: financeResponseLoadingDataSuccess,
    error: financeResponseDataError,
  } = useFinanceListHook(financeAppState.requestParams);

  const finalFinanceList = useMemo(() => {
    if (financeResponseLoading) return [];
    return financeResponse?.data ?? [];
  }, [financeResponse, financeResponseLoading]);

  useEffect(() => {
    if (financeResponseLoading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (financeResponseLoadingDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (financeResponseLoadingError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          financeResponseDataError?.response?.data.message ??
          ERROR_MESSAGE.GET_FINANCE_LIST_FAILURE,
      });
    }

    return () => {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    };
  }, [
    financeResponseLoading,
    financeResponseLoadingDataSuccess,
    financeResponseLoadingError,
    financeResponseDataError,
  ]);

  useEffect(() => {
    if (financeResponse?.data) {
      dispatch({
        type: ACTION_TYPE.UPDATE_FINANCE_LIST,
        payload: financeResponse.data,
      });
    }
  }, [financeResponse?.data]);

  const currentState = useMemo<TState>(
    () => ({
      financeAppState,
      financeList: finalFinanceList,
    }),
    [financeAppState, finalFinanceList]
  );

  return (
    <FinanceDispatchContext.Provider value={dispatch}>
      <FinanceContext.Provider value={currentState}>
        {children}
      </FinanceContext.Provider>
    </FinanceDispatchContext.Provider>
  );
};

export const useFinanceState = () => {
  const state = useContext(FinanceContext);
  if (state === undefined) {
    throw new Error('useFinanceState must be used within a FinanceProvider');
  }
  return state;
};

export const useFinanceActions = () => {
  const dispatch = useContext(FinanceDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useFinanceActions dispatch must be used within a FinanceProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateFinanceList = (list: TFinancePrimary[]) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_FINANCE_LIST,
      payload: list,
    });
  };

  const setIsManageFinanceItemOpen = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_MANAGE_FINANCE_ITEM_OPEN,
      payload: isOpen,
    });
  };

  const setSelectedFinanceItem = (item: TFinanceDetails | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_FINANCE_ITEM, payload: item });
  };

  const setIsFinanceItemLoading = (isLoading: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_FINANCE_ITEM_LOADING,
      payload: isLoading,
    });
  };

  const setIsFinanceItemError = (isError: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_FINANCE_ITEM_ERROR, payload: isError });
  };

  const setFinanceItemErrorMessage = (errorMessage: string) => {
    dispatch({
      type: ACTION_TYPE.SET_FINANCE_ITEM_ERROR_MESSAGE,
      payload: errorMessage,
    });
  };

  const setSelectedFinanceItemId = (itemId: string | null) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_FINANCE_ITEM_ID,
      payload: itemId,
    });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  return {
    updateSearchParams,
    updateFinanceList,
    setIsManageFinanceItemOpen,
    setSelectedFinanceItem,
    setIsFinanceItemLoading,
    setIsFinanceItemError,
    setFinanceItemErrorMessage,
    setSelectedFinanceItemId,
    setIsCreateMode,
    setIsEditMode,
  };
};
